/**
 * Created by neo on 23.03.21.
 */
import { RecurringPattern } from './RecurringPattern';
import { durationOrder, NotificationInterval } from './NotificationInterval';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { Pageable } from '../../Interfaces/Pageable';
import dayjs, { Dayjs } from 'dayjs';
import { Recipe } from '../../Diet/Recipe/Recipe';
import { Activity } from '../../Activity/Activity';
import { ExploreEntry } from '../../Explore/ExploreEntry';
import { Audited, createAuditedFields } from '../../Audited';
import { getMediaMediumOrSmallest } from '../../Media/Media';

export type EventEntrySearchParams = Pageable & {
  athleteId?: string;
  startDateTime?: Date;
  endDateTime?: Date;
  objectType?: string;
  objectId?: string;
  parent?: boolean;
  hideCompleted?: boolean;
  sourceType?: string;
  sourceId?: string;
};

export type EventEntry = Audited & {
  athleteId: string;
  parentId?: string;
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  timezone: string;
  fullDay: boolean;
  objectType?: string;
  objectId?: string;
  sourceType?: string;
  sourceId?: string;
  recurringPattern?: RecurringPattern;
  deletable: boolean;
  hidden: boolean;
  completedAt?: string;
  linkedData: Record<string, any>;
  notificationIntervals: NotificationInterval[];
  notificationEnabled: boolean;
  aiPlanned: boolean;
  groupId?: string;
  parent?: EventEntry;
  object?: Recipe | Activity | ExploreEntry;
};

// --- Utility functions ---

export function getEventTitle(e: EventEntry): string | undefined {
  return e.notificationIntervals.length > 0 ? e.notificationIntervals[0].title : undefined;
}

export function getEventMessage(e: EventEntry): string | undefined {
  return e.notificationIntervals.length > 0 ? e.notificationIntervals[0].message : undefined;
}

export function getEventImage(e: EventEntry): string | undefined {
  const media = e.object?.media;
  return media ? getMediaMediumOrSmallest(media) : undefined;
}

export function getEventLink(e: EventEntry): string | undefined {
  if (e.object && 'type' in e.object) {
    return `https://coach.kinastic.com/explore/${e.object.type}/${e.object.id}?eventId=${e.id}`;
  } else if (e.object && 'identifier' in e.object) {
    return `https://coach.kinastic.com/activityLogEditByActivity/${e.object.identifier}?eventId=${e.id}`;
  }
  return undefined;
}

export function isEventSingle(e: EventEntry): boolean {
  return !e.recurringPattern;
}

export function fixTime(time?: string | null): string | undefined {
  if (time) {
    if (time.length === 5) {
      return `${time}:00`;
    } else if (time.length >= 8) {
      return time.substring(0, 8);
    }
  }
  return undefined;
}

export function getEventStartDateTime(e: EventEntry): Dayjs {
  const time = fixTime(e.startTime) ?? '00:00';
  return dayjs(`${e.startDate} ${time}`, 'YYYY-MM-DD HH:mm');
}

export function getEventEndDateTime(e: EventEntry): Dayjs {
  const time = fixTime(e.endTime) ?? '00:00';
  return dayjs(`${e.endDate} ${time}`, 'YYYY-MM-DD HH:mm');
}

export function getEventDurationMinutes(e: EventEntry): number {
  return dayjs.duration(getEventEndDateTime(e).diff(getEventStartDateTime(e))).asMinutes();
}

export function getEventEarliestInterval(e: EventEntry): NotificationInterval | undefined {
  return e.notificationIntervals
    .slice()
    .sort((a, b) => durationOrder.indexOf(b.durationBefore) - durationOrder.indexOf(a.durationBefore))[0];
}

export function getEventLatestInterval(e: EventEntry): NotificationInterval | undefined {
  return e.notificationIntervals
    .slice()
    .sort((a, b) => durationOrder.indexOf(a.durationBefore) - durationOrder.indexOf(b.durationBefore))[0];
}

export function getEventLocalStartDateTime(e: EventEntry): Dayjs | undefined {
  const startDt = getEventStartDateTime(e);
  return startDt ? dayjs(startDt.toDate()) : undefined;
}

export function getEventName(e: EventEntry, language: string = 'de'): string | undefined {
  return e.object?.getName(language);
}

export function eventWithoutRecurring(e: EventEntry): EventEntry {
  return { ...e, recurringPattern: undefined };
}

/**
 * Extracts only plain data fields from an EventEntry, stripping computed getters
 * and methods that MobX observables may include. Use this before posting to the API.
 */
export function toEventEntryPayload(e: EventEntry): Record<string, any> {
  return {
    id: e.id,
    createdAt: e.createdAt,
    updatedAt: e.updatedAt,
    createdBy: e.createdBy,
    updatedBy: e.updatedBy,
    athleteId: e.athleteId,
    parentId: e.parentId,
    startDate: e.startDate,
    endDate: e.endDate,
    startTime: e.startTime,
    endTime: e.endTime,
    timezone: e.timezone,
    fullDay: e.fullDay,
    objectType: e.objectType,
    objectId: e.objectId,
    sourceType: e.sourceType,
    sourceId: e.sourceId,
    recurringPattern: e.recurringPattern
      ? {
          type: e.recurringPattern.type,
          endDate: e.recurringPattern.endDate,
          maxOccurrences: e.recurringPattern.maxOccurrences,
          separationCount: e.recurringPattern.separationCount,
          daysOfWeek: [...e.recurringPattern.daysOfWeek],
          weekOfMonth: e.recurringPattern.weekOfMonth,
          dayOfMonth: e.recurringPattern.dayOfMonth,
          monthOfYear: e.recurringPattern.monthOfYear,
          nthDayOfTheMonth: e.recurringPattern.nthDayOfTheMonth,
          weekDayOfMonth: e.recurringPattern.weekDayOfMonth,
        }
      : undefined,
    deletable: e.deletable,
    hidden: e.hidden,
    completedAt: e.completedAt,
    linkedData: e.linkedData ? { ...e.linkedData } : {},
    notificationIntervals: e.notificationIntervals.map((i) => ({
      title: i.title,
      message: i.message,
      durationBefore: i.durationBefore,
      customData: i.customData ? { ...i.customData } : {},
      sentAt: i.sentAt,
      requestId: i.requestId,
    })),
    notificationEnabled: e.notificationEnabled,
    aiPlanned: e.aiPlanned,
    groupId: e.groupId,
  };
}

/**
 * Creates a default EventEntry with sensible defaults. Use for initializing new events.
 */
export function defaultEventEntry(overrides?: Partial<EventEntry>): EventEntry {
  return {
    ...createAuditedFields(overrides),
    athleteId: overrides?.athleteId ?? '',
    parentId: overrides?.parentId,
    startDate: overrides?.startDate ?? '',
    startTime: overrides?.startTime,
    endDate: overrides?.endDate ?? '',
    endTime: overrides?.endTime,
    timezone: overrides?.timezone ?? 'Europe/Zurich',
    fullDay: overrides?.fullDay ?? false,
    objectType: overrides?.objectType,
    objectId: overrides?.objectId,
    sourceType: overrides?.sourceType,
    sourceId: overrides?.sourceId,
    recurringPattern: overrides?.recurringPattern,
    deletable: overrides?.deletable ?? true,
    hidden: overrides?.hidden ?? false,
    completedAt: overrides?.completedAt,
    linkedData: overrides?.linkedData ?? {},
    notificationEnabled: overrides?.notificationEnabled ?? true,
    notificationIntervals: overrides?.notificationIntervals ?? [],
    aiPlanned: overrides?.aiPlanned ?? false,
    groupId: overrides?.groupId,
    parent: overrides?.parent,
    object: overrides?.object,
  };
}

export const EventEntryApi = {
  find(params: EventEntrySearchParams): Promise<EventEntry[]> {
    return HttpBackend.get('/coach/schedule/event', params).then((res) => (res ?? []).map((r: any) => defaultEventEntry(r)));
  },

  findExisting(params: EventEntrySearchParams): Promise<EventEntry[]> {
    return HttpBackend.get('/coach/schedule/event', Object.assign(params, { size: 1 })).then((res) =>
      (res ?? []).map((r: any) => defaultEventEntry(r)),
    );
  },

  findParent(params: EventEntrySearchParams): Promise<EventEntry | undefined> {
    return HttpBackend.get('/coach/schedule/event', Object.assign(params, { parent: true, size: 1 })).then((res) => {
      if ((res ?? []).length > 0) {
        const event = defaultEventEntry(res[0]);
        if (
          event.recurringPattern ||
          Number(getEventStartDateTime(event).format('YYYYMMDD')) >= Number(dayjs().format('YYYYMMDD'))
        ) {
          return event;
        }
      }
      return undefined;
    });
  },

  findOne(id: string): Promise<EventEntry> {
    return HttpBackend.get(`/coach/schedule/event/${id}`).then((res) => defaultEventEntry(res));
  },

  complete(eventEntry: EventEntry, linkedData: Record<string, any> = {}): Promise<EventEntry> {
    return HttpBackend.post(`/coach/schedule/event/${eventEntry.id}/log`, {
      linkedData,
      date: new Date().toISOString(),
    }).then(() => eventEntry);
  },
};
